import { Injectable } from '@angular/core';
import { capSQLiteChanges, capSQLiteValues } from '@capacitor-community/sqlite';
import {
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  DEFAULT_MAP_VIEWPORT_DISTANCE,
  IBoondockPoint,
  IBoondockSearchQuery,
  LatLon,
} from '@curbnturf/entities';
import { Logger } from '../../log/logger';
import { DatabaseService } from '../database.service';
import { IDbSet } from '../dbset.interface';

const TABLE_NAME = 'boondock';

@Injectable({
  providedIn: 'root',
})
export class BoondockDbSet implements IDbSet {
  constructor(
    private databaseService: DatabaseService,
    private logger: Logger,
  ) {}

  public store(request: IBoondockPoint): Promise<capSQLiteValues> {
    return this.databaseService.insert(TABLE_NAME, this.boondockObjectsToStorable([request]).pop());
  }

  public storeArray(requests: IBoondockPoint[]): Promise<capSQLiteValues> {
    return this.databaseService.insertMulti(TABLE_NAME, this.boondockObjectsToStorable(requests));
  }

  public async truncateAndStoreArray(records: IBoondockPoint[]): Promise<capSQLiteValues> {
    records = this.boondockObjectsToStorable(records);
    await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
    const { statement, values } = this.databaseService.generateInsertStatement(TABLE_NAME, records);
    this.logger.debug('Executing INSERT STATEMENT', statement);
    return this.databaseService.query(statement, values);
  }

  public async truncate(): Promise<capSQLiteChanges> {
    return await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
  }

  public async retrieve(id: number): Promise<IBoondockPoint> {
    const results = await this.databaseService.select(TABLE_NAME, { id });
    const objResults = this.boondockResultsObject(results);
    if (objResults && objResults?.length > 0) {
      return objResults[0];
    } else {
      return {
        coordinates: { lat: 0, lon: 0 },
        distance: 0,
        location: '',
        name: '',
        photos: [],
        rating: 0,
        ratingCount: 0,
        id,
      };
    }
  }

  public async retrieveAll(limit?: number): Promise<IBoondockPoint[]> {
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    return this.boondockResultsObject(results);
  }

  public async retrieveOwnBoondock(limit?: number): Promise<IBoondockPoint[]> {
    // @todo filter this list to only owned listings.
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    return this.boondockResultsObject(results);
  }

  public async retrieveFiltered(
    query: IBoondockSearchQuery,
    take: number = 1000,
    skip: number = 0,
  ): Promise<IBoondockPoint[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const values: any[] = [];
    let statement = `SELECT * FROM ${TABLE_NAME} WHERE `;

    const wheres: string[] = [];

    // String Filter
    if (query.string) {
      wheres.push('(name LIKE ? OR location LIKE ?)');
      values.push('%' + query.string + '%', '%' + query.string + '%');
    }

    // Viewport Filter
    const { nwPoint, sePoint } = this.getViewportPoints(query);

    if (nwPoint && sePoint) {
      let clause = 'lat < ? AND lng > ?';
      values.push(nwPoint.lat, nwPoint.lon);
      clause += 'AND lat > ? AND lng < ?';
      values.push(sePoint.lat, sePoint.lon);
      wheres.push(clause);
    }

    statement += wheres.join(' AND ');

    statement += ` LIMIT ? OFFSET ?;`;
    values.push(take, skip);

    this.logger.debug('Filtered SELECT query executing', { statement, values });

    return this.boondockResultsObject(await this.databaseService.query(statement, values));
  }

  public async remove(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { id });
  }

  public async removeBySyncId(syncId: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { sync_id: syncId });
  }

  private getViewportPoints(query: IBoondockSearchQuery) {
    let nwPoint;
    let sePoint;
    if (query.lat && query.lon) {
      const point = new LatLon({ lat: query.lat, lon: query.lon });
      const distance = query.distance ? query.distance : DEFAULT_MAP_VIEWPORT_DISTANCE;
      const bearingDistance = Math.sqrt(2 * Math.pow(distance, 2));
      nwPoint = point.destinationPoint(bearingDistance, 315);
      sePoint = point.destinationPoint(bearingDistance, 135);
    } else {
      const point = new LatLon({ lat: DEFAULT_MAP_LATITUDE, lon: DEFAULT_MAP_LONGITUDE });
      const distance = query.distance ? query.distance : DEFAULT_MAP_VIEWPORT_DISTANCE;
      const bearingDistance = Math.sqrt(2 * Math.pow(distance, 2));
      nwPoint = point.destinationPoint(bearingDistance, 315);
      sePoint = point.destinationPoint(bearingDistance, 135);
    }

    return { nwPoint, sePoint };
  }

  private boondockResultsObject(results: capSQLiteValues): IBoondockPoint[] {
    if (results.values) {
      return results.values?.map((record) => {
        return {
          ...record,
          lat: undefined,
          lng: undefined,
          coordinates: { lat: record.lat, lon: record.lng },
          photos: [],
        };
      });
    } else {
      return [];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private boondockObjectsToStorable(items: IBoondockPoint[]): any[] {
    if (items) {
      return items?.map((record: IBoondockPoint) => {
        return {
          // Can't use spread operator here as it tries to enter null in undefined properties as columns
          id: record.id,
          name: record.name,
          lat: record.coordinates.lat,
          lng: record.coordinates.lon,
          location: record.location,
          rating: record.rating,
          ratingCount: record.ratingCount,
        };
      });
    } else {
      return [];
    }
  }
}
